import * as React from 'react';
import { css } from '@emotion/core';
import { FLEX_COLUMN, FLEX_CENTERED, FLEX_ROW } from '../../../layout/flexBox';
import { DevPlanAction } from '../models';
import LocalSelect from '../../../localization/LocalSelect';
import { SELECT } from '../../../forms/styles/select';
import { COLORS } from '../../../globalStyle/colors';
import arrow from '../../../assets/triangle-down-white.svg';
import LocalDiv from '../../../localization/LocalDiv';
import { useActionStatus, useCompletionDate } from './hooks';
import AddToCalendarBtn from '../../../forms/AddToCalendarBtn';
import Spacer from '../../../layout/Spacer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThermometerHalf, faHourglassEnd } from '@fortawesome/free-solid-svg-icons';
import { faCalendar } from '@fortawesome/free-regular-svg-icons';
import { Str } from '../../../localization/translationKeys';
import { BTN_HOVER, BTN_GENERAL } from '../../../forms/styles/button';
import MobileDatePicker from '../../../forms/MobileDatePicker';
import { useToggle } from '../../../core/hooks/useToggle';
import Msg from '../../../localization/Msg';
import { BENCHMARKS } from '../../../language/time';


const styles = {
  root:         [
    FLEX_COLUMN,
    css`
      align-items: stretch;
      padding-bottom: 30px;
    `,
  ],
  disabled:     css`
    opacity: .4;
  `,
  btnRow:       css`
    display: flex;
    flex-direction: row;
    align-items: center;
  `,
  icons:        {
    wrapper:   [
      FLEX_ROW,
      FLEX_CENTERED,
      css`
        width: 22px;
      `,
    ],
    hourGlass: css`
      color: ${COLORS.BLUE};
      width: 22px;
      font-size: 18px;
    `,
    cal:       css`
      color: ${COLORS.BLUE};
      width: 22px;
      font-size: 18px;
    `,
    status:    css`
      font-size: 22px;
    `,
  },
  datePicker:   {
    wrapper: [
      css`
        /* Btn hover adds a border which moves contents onHover... this fixes that. */
        border: 1px solid transparent;
      `,
      BTN_GENERAL,
      BTN_HOVER,
      css`
        display: flex;
        flex-direction: row;
        align-items: center;
        background-color: ${COLORS.BLUE};
        color: ${COLORS.WHITE};
        padding: 0 12px;
        flex-grow: 1;
      `,
    ],
    picker:  css`
      color: ${COLORS.WHITE};
      background-color: ${COLORS.BLUE};
      border: none;
      outline: none;
      text-align: start;
      
      &:hover {
        cursor: pointer;
        color: ${COLORS.HOVER_GREY};
      }
    `,
  },
  addToCalBtn:  [
    css`
      flex-grow: 1;
    `,
  ],
  statusSelect: [
    SELECT,
    css`
      color: ${COLORS.WHITE};
      background-image: url(${arrow});
      user-select: none;
      flex-grow: 1;
    `,
  ],
  addDate: [
    css`
        /* Btn hover adds a border which moves contents onHover... this fixes that. */
        border: 1px solid transparent;
      `,
    BTN_GENERAL,
    BTN_HOVER,
    css`
        display: flex;
        flex-direction: row;
        align-items: center;
        background-color: ${COLORS.BLUE};
        color: ${COLORS.WHITE};
        padding: 0 12px;
        flex-grow: 1;
      `,
  ],
};


type Props = {
  action: DevPlanAction;
  className?: string;
};


/**
 * MobileActionBtns: a component that renders the mobile version of the buttons for each dev plan item action. These
 * buttons do things like change the goal date, add the goal date to calendar and change the action status.
 */
const MobileActionBtns: React.FC<Props> = props => {
  const actionStatus = useActionStatus(props.action.devItemId, props.action.devActionId, props.action.status);
  const completeBy   = useCompletionDate(props.action.devItemId, props.action.devActionId, props.action.goalDate);

  const [startOpen, toggleStartOpen] = useToggle(false);

  const initializeDate = () => {
    completeBy.onChangeDate(new Date());
    toggleStartOpen();
  };
  
  return (
    <div css={styles.root} className={props.className}>
      <LocalDiv css={styles.btnRow}>
        {
          completeBy.selectedDate.valueOf() === BENCHMARKS.Y2K.valueOf()
          ? (
            <LocalDiv css={styles.addDate} onClick={initializeDate}>
              <Msg str={Str.CompleteBy}/>
            </LocalDiv>
          ) : (
            <MobileDatePicker
              msg={Str.CompleteBy}
              selectedDate={completeBy.selectedDate}
              onChangeDate={completeBy.onChangeDate}
              disabled={completeBy.loading}
              css={[completeBy.loading && styles.disabled]}
              startOpen={startOpen}
            />
          )
        }
        <Spacer />
        <div css={styles.icons.wrapper}>
          <FontAwesomeIcon icon={faHourglassEnd} css={styles.icons.hourGlass} />
        </div>
      </LocalDiv>
      <Spacer sizePx={8} />

      <LocalDiv css={styles.btnRow}>
        <AddToCalendarBtn selectedDate={completeBy.selectedDate} css={styles.addToCalBtn} />
        <Spacer />
        <div css={styles.icons.wrapper}>
          <FontAwesomeIcon icon={faCalendar} css={styles.icons.cal} />
        </div>
      </LocalDiv>
      <Spacer sizePx={8} />

      <LocalDiv css={styles.btnRow}>
        <LocalSelect
          value={actionStatus.selected}
          onChange={actionStatus.onChange}
          disabled={actionStatus.loading}
          css={[styles.statusSelect, css`background-color: ${actionStatus.color};`]}
        >
          {
            actionStatus.statuses.map(status => (
              <option
                key={status.statusId}
                value={status.statusId}
                label={status.txt}
              >{status.txt}</option>
            ))
          }
        </LocalSelect>
        <Spacer />
        <div css={styles.icons.wrapper}>
          <FontAwesomeIcon icon={faThermometerHalf} css={[styles.icons.status, css`color: ${actionStatus.color};`]} />
        </div>
      </LocalDiv>
    </div>
  );
};

export default MobileActionBtns;
